import React, { useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
    Grid,
    Typography,
    Icon,
    Tooltip,
    Box,
    CircularProgress,
} from '@mui/material'
import i18n from 'simple-react-i18n'
import { isEmpty, map, template } from 'lodash'
import FtpItemMenu from './FtpItemMenu'
import { Folder } from '@mui/icons-material'
import { getIconFromFileType, getColorFromFileType, getFolderTooltip } from './FtpUtils'

const cardSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1,
    borderRadius: 1,
    position: 'relative',
    bgcolor: 'transparent',
    transition: 'background-color .15s',
    '&:hover': {
        bgcolor: 'action.hover',
        '& > :last-child': { opacity: 1 },
    },
}

const FolderCard = ({ item, onNavigate, onReload }) => {
    const title = getFolderTooltip(item)

    return (
        <Tooltip title={title} >
            <Box
                onClick={() => onNavigate(item)}
                sx={{ ...cardSx, cursor: 'pointer' }}
            >
                <Folder sx={{ fontSize: '3rem', color: getColorFromFileType('folder'), flexShrink: 0 }} />
                <Typography
                    variant='body2'
                    noWrap
                    sx={{ flex: 1, fontWeight: 400, color: 'text.primary', fontSize: '0.85rem' }}
                >
                    {item.label}
                </Typography>
                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        opacity: 0,
                        transition: 'opacity .15s',
                        position: 'absolute',
                        right: 1,
                    }}
                >
                    <FtpItemMenu item={item} onReload={onReload} />
                </Box>
            </Box>
        </Tooltip>
    )
}

FolderCard.propTypes = {
    item: PropTypes.object.isRequired,
    onNavigate: PropTypes.func.isRequired,
    onReload: PropTypes.func.isRequired,
}

const FileCard = ({ item, onReload }) => {
    const iconName = getIconFromFileType(item.fileType)
    return (
        <Tooltip title={item.label} >
            <Box sx={{ ...cardSx, cursor: 'default' }}>
                <Icon sx={{ fontSize: '3rem', color: getColorFromFileType(item.fileType), flexShrink: 0 }}>{iconName}</Icon>
                <Typography
                    variant='body2'
                    noWrap
                    sx={{ flex: 1, fontWeight: 400, color: 'text.primary', fontSize: '0.85rem' }}
                >
                    {item.label}
                </Typography>
                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        opacity: 0,
                        transition: 'opacity .15s',
                        position: 'absolute',
                        right: 1,
                    }}
                >
                    <FtpItemMenu item={item} onReload={onReload} />
                </Box>
            </Box>
        </Tooltip>
    )
}

FileCard.propTypes = {
    item: PropTypes.object.isRequired,
    onReload: PropTypes.func.isRequired,
}

const FtpGridView = ({
    folders,
    files,
    visibleCount = files.length,
    onLoadMore,
    containerRef,
    onNavigate,
    onReload,
}) => {
    const sentinelRef = useRef(null)
    const visibleFiles = useMemo(() => files.slice(0, visibleCount), [files, visibleCount])
    const hasMore = visibleFiles.length < files.length

    useEffect(() => {
        const sentinel = sentinelRef.current
        if (sentinel && hasMore && onLoadMore) {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0]?.isIntersecting) {
                        onLoadMore()
                    }
                },
                {
                    root: containerRef?.current || null,
                    rootMargin: '250px',
                    threshold: 0,
                },
            )

            observer.observe(sentinel)
            return () => observer.disconnect()
        }
        return undefined
    }, [hasMore, onLoadMore, containerRef, visibleCount])

    return (
        <>
            {!isEmpty(folders) && (
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant='body2'
                        sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5, fontSize: '0.85rem' }}
                    >
                        {i18n.folders}
                    </Typography>
                    <Grid container spacing={1.5}>
                        {map(folders, (item) => (
                            <Grid key={item.path} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                                <FolderCard
                                    item={item}
                                    onNavigate={onNavigate}
                                    onReload={onReload}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
            {!isEmpty(files) && (
                <Box>
                    <Typography
                        variant='body2'
                        sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5, fontSize: '0.85rem' }}
                    >
                        {i18n.files}
                    </Typography>
                    <Grid container spacing={1.5}>
                        {map(visibleFiles, (item) => (
                            <Grid key={item.path} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                                <FileCard
                                    item={item}
                                    onReload={onReload}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {hasMore && (
                        <Grid
                            ref={sentinelRef}
                            container
                            direction='column'
                            alignItems='center'
                            justifyContent='center'
                            py={3}
                            spacing={1}
                        >
                            <CircularProgress size={26} color='primary' />
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {template(i18n.ftpShowingCount)({
                                    current: visibleFiles.length,
                                    total: files.length,
                                })}
                            </Typography>
                        </Grid>
                    )}
                </Box>
            )}
        </>
    )
}

FtpGridView.propTypes = {
    folders: PropTypes.array.isRequired,
    files: PropTypes.array.isRequired,
    visibleCount: PropTypes.number,
    onLoadMore: PropTypes.func,
    containerRef: PropTypes.object,
    onNavigate: PropTypes.func.isRequired,
    onReload: PropTypes.func.isRequired,
}

export default FtpGridView
